import { CreateCampaignPayload, CreateLineItemPayload } from 'shared/src/beeswax/beeswax-types';
import type { AdapterCapabilities, AdapterConfig, AdapterSchemas } from '../skill-platform-types';

export const beeswaxAdapterConfig: AdapterConfig = {
  platformName: 'Beeswax',
  platformShortLabel: 'BW',
  fullHierarchy: [
    { key: 'advertiser', label: 'Advertiser' },
    { key: 'campaign', label: 'Campaign' },
    { key: 'line_item', label: 'Line Item' }
  ],
  createSequence: ['campaign', 'line_item'],
  requiredAncestors: ['advertiser'],
  rootEntityType: 'advertiser',
  mediaBuyEntityType: 'line_item',
  workflowSteps: ['Configure', 'Review']
};

// Prod Beeswax UI host. Non-prod (sandbox) is e.g. https://demosbx.beeswax.com — configured per
// environment via BEESWAX_UI_BASE_URL and supplied by the backend skill as `baseUrl`.
export const BEESWAX_PROD_UI_BASE_URL = 'https://brkthru.beeswax.com';

export function buildBeeswaxEntityUrl(
  entityType: string,
  entityId: string,
  advertiserId?: string,
  baseUrl: string = BEESWAX_PROD_UI_BASE_URL
): string {
  const base = baseUrl.replace(/\/$/, '');
  if (entityType === 'campaign') {
    return `${base}/line_items?resource__advertiser_id__in=${advertiserId ?? ''}&resource__campaign_id__in=${entityId}`;
  }
  if (entityType === 'line_item') {
    return `${base}/line_items/${entityId}`;
  }
  return `${base}/`;
}

export const beeswaxAdapterSchemas: AdapterSchemas = {
  entityPayloads: {
    campaign: CreateCampaignPayload,
    line_item: CreateLineItemPayload
  }
};

export const beeswaxCapabilities: AdapterCapabilities = {
  lineItemTypes: ['banner', 'video', 'native'],
  lineItemTypeAliases: {
    banner: ['display', 'html5'],
    video: ['ctv', 'ott', 'pre-roll', 'mid-roll'],
    native: ['in-feed', 'sponsored content']
  },
  requiresAccountId: true,
  accountLabel: 'advertiser',
  accountIdPlaceholder: 'e.g. 399728',
  biddingStrategies: ['CPM'],
  usesCpmBid: true,
  pacingOptions: ['lifetime', 'daily', 'flight', 'none'],
  budgetTypes: ['spend', 'impressions', 'spend including vendor fees'],
  targetingModules: [
    {
      key: 'geo.country',
      label: 'Country',
      kind: 'multi-select',
      optionsSource: 'static',
      staticOptions: [
        { id: '840', label: 'United States' },
        { id: '124', label: 'Canada' },
        { id: '826', label: 'United Kingdom' }
      ]
    },
    {
      key: 'geo.dma',
      label: 'DMA',
      kind: 'multi-select',
      optionsSource: 'zoho-derived',
      zohoField: 'Geo_Targets'
    },
    {
      key: 'device.type',
      label: 'Device',
      kind: 'multi-select',
      optionsSource: 'static',
      staticOptions: [
        { id: '0', label: 'Desktop' },
        { id: '1', label: 'Mobile' },
        { id: '2', label: 'Tablet' },
        { id: '3', label: 'Connected TV' }
      ]
    },
    {
      key: 'audience',
      label: 'Audience',
      kind: 'free-text',
      optionsSource: 'zoho-derived',
      zohoField: 'Target_Audience'
    }
  ]
};
